import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Page,
  Card,
  PageActions,
  TextField,
  FormLayout,
  Toast,
  Loading,
  Checkbox,
  Thumbnail,
  DropZone,
  InlineError,
  Text,
  Select,
} from "@shopify/polaris";

import axios from "../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../../Components/LoadingScreen";

const AddEditAward = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [fileError, setFileError] = useState("");

  const [item, setItem] = useState({
    title: "",
    subtitle: "",
    awarded_by: "",
    year: "",
    label: "",
    image: "",
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const imageKey = useRef("");

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((e) => ({ ...e, [field]: "" }));
    setFileError("");
  }, []);

  const [isImageUploading, setIsImageUploading] = useState(false);

  // ---------- FILE UPLOAD ----------
  const sendFile = (file) => {
    setIsImageUploading(true);
    const formData = new FormData();
    formData.append("entity_type", "case_study");
    formData.append("image", file);

    axios
      .post("/s3/save-file", formData)
      .then((res) => {
        imageKey.current = res.data.data.file_key;
        setItem((p) => ({
          ...p,
          image: URL.createObjectURL(file),
        }));
        setIsImageUploading(false);
      })
      .catch(console.error);
  };

  const handleImageDrop = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendFile(accepted[0]);
  }, []);

  // ----------- VALIDATION -----------
  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    if (!item.title.trim()) {
      newErrors.title = "This field is required";
      hasError = true;
    }
    if (!item.subtitle.trim()) {
      newErrors.subtitle = "This field is required";
      hasError = true;
    }
    if (!item.awarded_by.trim()) {
      newErrors.awarded_by = "This field is required";
      hasError = true;
    }
    if (!item.year.trim()) {
      newErrors.year = "This field is required";
      hasError = true;
    }
    if (!item.label.trim()) {
      newErrors.label = "This field is required";
      hasError = true;
    }
    // if (!item.image) {
    //   setFileError("Please upload an image");
    //   hasError = true;
    // }

    setErrors(newErrors);
    return !hasError;
  };

  // ---------- LOAD DATA IN EDIT MODE ----------
  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/awards/${id}`)
        .then((res) => {
          const d = res.data.data;

          setItem({
            title: d.title || "",
            subtitle: d.subtitle || "",
            awarded_by: d.awarded_by || "",
            year: d.year || "",
            label: d.label || "",
            // image: d.image_url || "",
            is_active: d.is_active ? true : false,
          });

          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, []);

  // ---------- SAVE ----------
  const handleSave = () => {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();

    if (mode === "edit") formData.append("_method", "PATCH");

    formData.append("title", item.title);
    formData.append("subtitle", item.subtitle);
    formData.append("awarded_by", item.awarded_by);
    formData.append("year", item.year);
    formData.append("label", item.label);
    formData.append("is_active", item.is_active ? 1 : 0);

    // if (imageKey.current) formData.append("image", imageKey.current);

    axios
      .post(mode === "new" ? "/awards" : `/awards/${id}`, formData)
      .then(() => navigate("/admin/awards"))
      .catch(console.error)
      .finally(() => setIsSaving(false));
  };

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Award`}
      breadcrumbs={[{ onAction: () => navigate("/admin/awards") }]}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          {/* -------- IMAGE -------- */}
          {/* <Text>Image</Text>
          <div style={{ display: "flex" }}>
            <Thumbnail size="large" source={item.image || ""} />

            <div style={{ width: "100%", marginLeft: 10 }}>
              <DropZone
                accept=".png,.jpg,.jpeg,.webp"
                allowMultiple={false}
                onDrop={handleImageDrop}
              >
                <DropZone.FileUpload actionHint="Upload an image" />
              </DropZone>
            </div>
          </div>
          {fileError && <InlineError message={fileError} />} */}

          {/* -------- TITLE -------- */}
          <TextField
            label="Title"
            value={item.title}
            onChange={(v) => handleChange("title", v)}
            error={errors.title}
          />

          {/* -------- SUBTITLE -------- */}
          <TextField
            label="Subtitle"
            value={item.subtitle}
            onChange={(v) => handleChange("subtitle", v)}
            error={errors.subtitle}
          />

          {/* -------- AWARDED BY -------- */}
          <TextField
            label="Awarded By"
            value={item.awarded_by}
            onChange={(v) => handleChange("awarded_by", v)}
            error={errors.awarded_by}
          />

          {/* -------- YEAR -------- */}
          <TextField
            label="Year"
            type="number"
            value={item.year}
            onChange={(v) => handleChange("year", v)}
            error={errors.year}
          />

          {/* -------- LABEL -------- */}
          <Select
            label="Label"
            options={[
              { label: "Gold", value: "gold" },
              { label: "Silver", value: "silver" },
              { label: "Bronze", value: "bronze" },
              { label: "Crystal", value: "crystal" },
            ]}
            value={item.label}
            onChange={(v) => handleChange("label", v)}
            error={errors.label}
            placeholder="Select label"
          />

          {/* -------- ACTIVE -------- */}
          <Checkbox
            label="Is Active"
            checked={item.is_active}
            onChange={(v) => handleChange("is_active", v)}
          />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && [
            {
              content: "Delete",
              destructive: true,
              loading: isSaving && true,
              onClick: () => {
                setIsSaving(true);
                axios
                  .delete(`/awards/${id}`)
                  .then((result) => {
                    navigate(`/admin/awards`);
                  })
                  .catch((err) => console.log(err));
              },
            },
          ]
        }
      />

      {isImageUploading && <LoadingScreen />}
    </Page>
  );
};

export default AddEditAward;
